import { endOfWeek, nextWeek } from './calendar';
import { isDone } from './evaluate';
import {
  type FilterableItem,
  type FilterContext,
  type GroupBy,
  type SortField,
  type SortSpec,
  UNASSIGNED,
} from './model';

/** Sorting (TLB-4) and grouping (TLB-5) of work items. */

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

type SortValue = string | number | null;

function sortValue(item: FilterableItem, field: SortField, ctx: FilterContext): SortValue {
  switch (field) {
    case 'title':
      return item.title;
    case 'status':
      return ctx.labels.get(item.statusId)?.position ?? null;
    case 'priority':
      return item.priorityId === null ? null : (ctx.labels.get(item.priorityId)?.position ?? null);
    case 'owner': {
      const first = item.ownerIds[0];
      return first === undefined ? null : (ctx.personName?.(first) ?? first);
    }
    case 'dueDate':
      return item.dueDate;
    case 'timeline':
      return item.timeline?.start ?? null;
    case 'updated':
      return item.updatedAt;
    case 'created':
      return item.createdAt;
    case 'key':
      return Number(item.key.slice(item.key.lastIndexOf('-') + 1));
  }
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return collator.compare(String(a), String(b));
}

/**
 * Comparator for up to three sort keys. Empty values (no due date, no priority, no owner …) always
 * come last, whatever the direction. Ties return 0 so a stable sort keeps the manual order.
 */
export function compareItems(
  a: FilterableItem,
  b: FilterableItem,
  sort: SortSpec,
  ctx: FilterContext,
): number {
  for (const { field, dir } of sort) {
    const left = sortValue(a, field, ctx);
    const right = sortValue(b, field, ctx);
    if (left === null || right === null) {
      if (left !== right) return left === null ? 1 : -1;
      continue;
    }
    const result = compareValues(left, right);
    if (result !== 0) return dir === 'asc' ? result : -result;
  }
  return 0;
}

/** Due date buckets for "Group by: Due date" (TLB-5), in display order. */
export const DUE_BUCKETS = [
  { id: 'overdue', name: 'Overdue' },
  { id: 'today', name: 'Today' },
  { id: 'thisWeek', name: 'This week' },
  { id: 'nextWeek', name: 'Next week' },
  { id: 'later', name: 'Later' },
  { id: 'earlier', name: 'Done earlier' },
  { id: 'none', name: 'No date' },
] as const;

export type DueBucket = (typeof DUE_BUCKETS)[number]['id'];

/**
 * Overdue is past due and not done; done items due before today go to "Done earlier" (A12). "This
 * week" is the rest of the current week after today; weeks start on Monday (D27).
 */
export function dueBucketOf(item: FilterableItem, ctx: FilterContext): DueBucket {
  const due = item.dueDate;
  if (due === null) return 'none';
  if (due < ctx.today) return isDone(item, ctx) ? 'earlier' : 'overdue';
  if (due === ctx.today) return 'today';
  if (due <= endOfWeek(ctx.today)) return 'thisWeek';
  if (due <= nextWeek(ctx.today).to) return 'nextWeek';
  return 'later';
}

/** Section key for items without a priority (Group by: Priority). */
export const NO_PRIORITY = 'none';

/**
 * The sections an item belongs to. Usually one; with "Group by: Owner" an item with several owners
 * appears under each of them (D34), and an item without owners under "Unassigned".
 */
export function sectionsOf(item: FilterableItem, groupBy: GroupBy, ctx: FilterContext): string[] {
  switch (groupBy) {
    case 'group':
      return [item.groupId];
    case 'status':
      return [item.statusId];
    case 'priority':
      return [item.priorityId ?? NO_PRIORITY];
    case 'owner':
      return item.ownerIds.length > 0 ? [...item.ownerIds] : [UNASSIGNED];
    case 'dueBucket':
      return [dueBucketOf(item, ctx)];
  }
}
